import React, {useCallback, useEffect, useState} from 'react';
import './app.scss';
import {Redirect, Route, Router, Switch, useHistory, useLocation} from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Header from '../components/header/header';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {useDispatch, useSelector} from 'react-redux';
import {loading as PatientLoading, loadingAdmission, processing as PatientProcessing} from '../store/reducers/patient';
import {loading as DocumentLoading} from '../store/reducers/documents';
import {loading as PatientAdditionalProcessing} from '../store/reducers/additionalData';
import makeStyles from '@mui/styles/makeStyles';
import {agylePref, loadPref} from '../store/reducers/preferences';
import {
    canEnterAdminPage,
    hasPermission,
    loadingUser,
    loadUser,
    Permissions,
    setLoadingUser,
    user,
} from '../store/reducers/user';
import {loadExternalLinks} from '../store/reducers/externalLinks';
import {SessionTimer} from '../components/header/sessionTimer';
import {setIpadSetting} from '../store/reducers/workflow';
import _ from 'lodash';
import ExtendedDashboard from '../pages/reports/extendedDashboard';
import {loadDicts} from '../store/reducers/dicts';
import MainDashboard from '../pages/mainDashboard';
import {AgyleNamedIconView} from '../components/icons/agyle';
import {isDesktop} from 'react-device-detect';
import {loadWardList, setMapHospitalList, setMultiHospitalMode, updateMap} from '../store/reducers/ward';
import {isSpecialSmallDeviceDetect} from '../util/isSpecialSmallDeviceDetect';
import WardDashboard from '../pages/wardDashboard/wardDashboard';
import AgyleHeader from '../components/header/agyleHeader/agyleHeader';
import EstimatedWaitingTime from '../pages/reports/estimatedWaitingTime';
import clsx from 'clsx';
import {loadExternalConfigs} from '../store/reducers/externalConfig';
import Page404 from '../pages/error/page404';
import ServiceTemporarilyUnavailable from '../components/other/serviceTemporarilyUnavailable';
import useAgyleSnackbar from '../hooks/useAgyleSnackbar';
import API from './api';
import {setSnackbar} from '../store/reducers/globals';
import {BMLoading} from '../store/reducers/bedManagement';
import {CENTRAL_PAGE_PATH, MODULE_NAMES, MODULE_PATHS} from '../pages/centralPage/constants';
import {isCentralPageModuleEnabled} from '../pages/centralPage/helper';
import {getConfig} from '../util/configUtil';
import DashboardRoutes from './extraRoute/DashboardRoutes';
import MainLoadingScreen from '../components/loading/mainLoadingScreen';
import PasConnectionChecker from '../components/modal/pasConnectionChecker';

const useStyles = makeStyles(() => ({
    backdrop: {
        zIndex: 9999,//theme.zIndex.drawer + 1,
    },
}));

// Lazy loading pages
const PatientContext = React.lazy(() => import('../pages/patient/patientContext/patientContext'));
const Maintenance = React.lazy(() => import("../pages/utils/maintenance"));
const Patients = React.lazy(() => import("../pages/patients/patients"));
const PatientSearch = React.lazy(() => import('../pages/patients/patientSearch'));
const Patient = React.lazy(() => import("../pages/patient/patient"));
const Audits = React.lazy(() => import("../pages/audit/audit"));
const WardView = React.lazy(() => import("../pages/ward/view"));
const WardViewNew = React.lazy(() => import("../pages/ward/map/wardView"));
const WardViewIpod = React.lazy(() => import("../pages/ward/ipod/view"));
const Viewer = React.lazy(() => import("../pages/viewer/viewer"));
const HoldingPen = React.lazy(() => import("../pages/holding/holdingPen"));
const WardMapCreator = React.lazy(() => import("../pages/wardCreator/wardMapCreator"));
const Registration = React.lazy(() => import("../pages/registration/registration"));
const AdmitPatientExt = React.lazy(() => import("../pages/admit/admitPatientExt"));
const AdmitPatientExtRefresh = React.lazy(() => import("../pages/admit/admitExtRefresh/admitPatientExt"));
const AdmitPatientNewNew = React.lazy(() => import('../pages/admit/admitPatientNewNew'));
const Admin = React.lazy(() => import("../pages/admin/admin"));
const WorkList = React.lazy(() => import("../pages/bookAdmission/workList"));
const BookedAdmissions = React.lazy(() => import("../pages/bookedAdmissions/bookedAdmissions"));
const BookedAdmissionsDashboard = React.lazy(() => import("../pages/bookedAdmissions/dashboard/dashboard"));
const BookAdmissionLanding = React.lazy(() => import("../pages/bookAdmission/bookAdmissionLanding"));
const BookAdmissionForm = React.lazy(() => import("../pages/bookAdmission/createBookAdmissionForm"));
const Tutorials = React.lazy(() => import("../pages/tutorial/tutorials"));
const BedBookingTable = React.lazy(() => import("../pages/bedBooking/bedBookingTable"));
const BedManagement = React.lazy(() => import("../pages/bedManagement/bedManagementContext"));
const CentralPage = React.lazy(() => import("../pages/centralPage/centralPage"));
const CleaningRequest = React.lazy(() => import("../pages/reports/cleaningRequestTable"));
const FHIRDashboard = React.lazy(() => import("../pages/admin/fhirsendadmin"));
const ActionPanel = React.lazy(() => import('../components/actionPanel/actionPanel'));
const AppointmentsRoot = React.lazy(() => import('../pages/appointments/appointmentsRoot'));

// Header disable
const BUILDS_EXCLUDED = ['agyle', 'main'];
const PATHS_EXCLUDED = [
    {path: '/', option: 'equal'},
    {path: '/main', option: 'equal'},
    {path: '/registration', option: 'equal'},
    {path: '/registry', option: 'equal'},
    {path: '/eadmit', option: 'equal'},
    {path: "/eadmitRefresh", option: "equal"},
    {path: '/report/', option: 'startsWith'},
    {path: '/ward', option: 'startsWith'},
    {path: '/admin', option: 'startsWith'},
    {path: '/icons', option: 'startsWith'},
    {path: '/bookAdmission', option: 'startsWith'},
    {path: '/createBookAdmission', option: 'startsWith'},
    {path: '/bookedAdmissions', option: 'startsWith'},
    {path: '/bookAdmission', option: 'startsWith'},
    {path: '/training', option: 'equal'},
    {path: '/bedBooking', option: 'equal'},
    {path: '/fhirDashboard', option: 'startsWith'},
    {path: '/patient/', option: 'startsWith'},
    {path: '/patient-context/', option: 'startsWith'},
    {path: '/bedManagement', option: 'startsWith'},
    {path: CENTRAL_PAGE_PATH, option: 'startsWith'},
    {path: "/patient/attendance/", option: "startsWith"},
    {path: CENTRAL_PAGE_PATH, option: "startsWith"},
    {path: MODULE_PATHS[MODULE_NAMES.SCHEDULER], option: 'startsWith'},
];

function App(props) {
    const history = useHistory();
    const classes = useStyles();
    const match = useLocation();
    const dispatch = useDispatch();

    // Snackbar listener
    useAgyleSnackbar();

    // Config
    const isUHDMap = getConfig('modules.map.customMap', false);
    const mctEnabled = getConfig('modules.wardCreator.enabled', false);
    const centralPageEnabled = getConfig('modules.centralPage.enabled', false);
    const bedManagementEnabled = getConfig('modules.bedManagement.enabled', false);
    const adminModuleEnabled = getConfig('adminModuleEnabled', false);
    const adminModuleEnabledExceptionUsers = getConfig('adminModuleEnabledExceptionUsers', []);
    const registrationEnabled = getConfig('admitPatient.registration', false);
    const externalManagmentEnabled = getConfig('externalManagment.enabled', false);
    const extendedDashboardEnabled = getConfig('modules.reports.extendedDashboard', false);
    const cleaningRequestEnabled = getConfig('modules.cleaningRequest.enabled', false);
    const bookAdmissionModuleEnabled = getConfig('bookAdmissionModuleEnabled', false);
    const pasCheckConnection = getConfig('modules.pasHealthCheck.enabled', false);

    // Selectors
    const userObject = useSelector(user);
    const loadingPatientObject = useSelector(PatientLoading);
    const loadingDocumentObject = useSelector(DocumentLoading);
    const processingPatientObject = useSelector(PatientProcessing);
    const processingPatientAdditionalObject = useSelector(PatientAdditionalProcessing);
    const bedManagementLoading = useSelector(BMLoading);
    const agylePreference = useSelector(agylePref);
    const loadingAdmissionObject = useSelector(loadingAdmission);
    const userDataLoading = useSelector(loadingUser);

    const canBooking = dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_BED_BOOKING));
    const [openFromLink, setOpenFromLink] = useState(false);

    const [isPatientClosed, setIsPatientClosed] = useState(false);
    useEffect(() => {
        if (openFromLink && !isPatientClosed) {
            const ec = new BroadcastChannel('eyonix_channel');
            ec.onmessage = (event) => {
                console.log(event);
                setIsPatientClosed(true);
                window.open('/logout.html', '_self');
            };
            ec.postMessage('opening');

            return function cleanup() {
                ec.close();
            };
        }
    }, [isPatientClosed, openFromLink]);

    const setNewToken = (data) => {
        if (data.token) window.auth.setToken(data.token);
    };

    const handleWindowMessage = (e) => {
        let messageType = e.data.type;

        switch (messageType) {
            case 'new-token':
                setNewToken(e.data);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        dispatch(loadDicts);
        if (!_.isEmpty(agylePreference?.customMap) && agylePreference?.switchMapMode !== 'central') {
            dispatch(updateMap(agylePreference?.customMap));
        }

        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.has('token')) {
            queryParams.delete('token');
            history.replace({
                search: queryParams.toString(),
            });
        }

        if (process.env.BUILD_MODULE === 'agyle') {
            // Checking whether to enable multi-hospital mode
            API.getHospitals('forMCT eq true')
                .then(data => {
                    dispatch(setMapHospitalList(data?.data?.__data));
                    dispatch(setMultiHospitalMode(data?.data?.__data?.length > 1));
                })
                .catch(err => {
                    dispatch(setSnackbar({
                        type: 'error',
                        message: 'There was a problem retrieving the list of hospitals.',
                    }));
                    console.error(err);
                });
        }

        window.addEventListener('message', handleWindowMessage);

        return () => {
            window.removeEventListener('message', handleWindowMessage);
        };
    }, []);

    if ((new URL(document.location)).searchParams.get('touch') === 'false') {
        dispatch(setIpadSetting({backend: false}));
    }

    const offGesture = () => {
        document.addEventListener('gesturestart', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });

        document.addEventListener('gesturechange', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });

        document.addEventListener('gestureend', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });
    };

    useEffect(() => {
        if (!match.pathname.startsWith('/registration')) {
            dispatch(setLoadingUser(true));
            dispatch(loadUser());
            dispatch(loadPref());
            if (process.env.BUILD_MODULE === 'agyle') {
                dispatch(loadWardList());
                dispatch(loadExternalConfigs());
            }
            if (!_.get(window, 'envConfig.externalSystemLinks.disabled', false)) {
                dispatch(loadExternalLinks());
            }
            offGesture();
        }

        if (match.search.includes('context')) {
            setOpenFromLink(true);
        }

        document.addEventListener('touchmove', preventDefaultEvent);
        return () => {
            document.removeEventListener('touchmove', preventDefaultEvent);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    const preventDefaultEvent = (e) => {
        // Disable mobile page refresh
        e.preventDefault();
    };

    function handleKeyPress(event) {
        if (event.code === 'KeyL' && event.ctrlKey) {
            event.preventDefault();
            window.auth.logout();
        }
    }

    const isSmallDevice = isSpecialSmallDeviceDetect();

    const getBodyHeight = () => {
        if (process.env.BUILD_MODULE === "agyle" && match && match.pathname &&
            (match.pathname.startsWith("/admin") || match.pathname.startsWith("/bookedAdmissions") || match.pathname.startsWith("/bedManagement")
              || match.pathname.startsWith('/report') || match.pathname === '/wards' || match.pathname === '/wardNew' || match.pathname === CENTRAL_PAGE_PATH ||
              match.pathname.startsWith(MODULE_PATHS[MODULE_NAMES.SCHEDULER])
            )) {
            return "100vh";
        }

        if ((process.env.BUILD_MODULE === 'agyle' && match && match.pathname && match.pathname.startsWith('/ward'))) {
            return 'initial';
        }
        if (isSmallDevice) return 'calc(100vh - 100px)';

        if (match.pathname === '/admin') {
            return '100vh';
        }
        let header = process.env.BUILD_MODULE === 'agyle' ? 70 : 53;
        return `calc(100vh - ${header}px)`;
    };

    const displayHeader = useCallback(() => {
        const builds = BUILDS_EXCLUDED.includes(process.env.BUILD_MODULE);
        let path = false;

        if (match && match.pathname) {
            _.forEach(PATHS_EXCLUDED, opt => {
                if (path) return;

                switch (opt.option) {
                    case 'startsWith':
                        path = match.pathname.startsWith(opt.path);
                        break;
                    case 'equal':
                    default:
                        path = match.pathname === opt.path;
                        break;
                }
            });
        }

        return !(builds && path);
    }, [match]);

    let redirect = isSmallDevice ? '/wardIpod/ED' : '/ward/ED';
    if (isUHDMap) redirect = '/main';
    if (isCentralPageModuleEnabled) redirect = CENTRAL_PAGE_PATH;

    const handleCheckPanel = () => {
        return !!(match?.pathname?.startsWith('/search'));
    }

    const renderSystemHeader = () => {
        if (process.env.BUILD_MODULE === 'agyle') {
            if(displayHeader())
                return <AgyleHeader displayLink showActionPanel={handleCheckPanel()}/>;
        } else {
            if(match.pathname !== '/')
                return <Header openFromLink={openFromLink}/>;
        }
        return null;
    };


    if (sessionStorage.getItem('maintenance')) {
        return (
            <Switch>
                <Route path="/">
                    <Maintenance/>
                </Route>
            </Switch>
        );
    }

    if (userDataLoading) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit" size={64}/>
            </Backdrop>
        );
    }

    return (
        <div id="app">
            <Backdrop
                className={classes.backdrop}
                open={!!loadingPatientObject || !!loadingDocumentObject || !!processingPatientObject || !!processingPatientAdditionalObject || !!bedManagementLoading || !!loadingAdmissionObject}
            >
                <CircularProgress color="inherit" size={64}/>
            </Backdrop>

            {(match?.pathname !== '/registration' && !props.isFrame) && <SessionTimer/>}

            {renderSystemHeader()}

            {process.env.BUILD_MODULE === 'agyle' &&
                <>
                    <ServiceTemporarilyUnavailable/>
                    <ActionPanel/>
                    {pasCheckConnection && <PasConnectionChecker/>}
                </>
            }

            <div className={clsx(process.env.BUILD_MODULE === 'main' && 'eyonix', process.env.BUILD_MODULE === 'agyle' && 'agyle')} style={{height: getBodyHeight()}}>
                <PerfectScrollbar ref={(ref) => {
                    window._scrollBarRef = ref;
                }}>
                    {/* That will be APPBAR there and we need to add offset here because this will be static ! */}
                    <React.Suspense fallback={<MainLoadingScreen/>}>
                        <Router history={history}>
                            <Switch>
                                <Route path="/ward/:wardId">
                                    <WardView/>
                                </Route>
                                {mctEnabled &&
                                    <Route path="/wardNew/:mapId">
                                        <WardViewNew/>
                                    </Route>
                                }

                                {mctEnabled &&
                                    <Route path="/wards">
                                        <WardDashboard/>
                                    </Route>
                                }

                                {centralPageEnabled &&
                                    <Route path={CENTRAL_PAGE_PATH}>
                                        <CentralPage/>
                                    </Route>
                                }

                                <Route path="/patient/attendance/:attId">
                                    <PatientContext standaloneMode/>
                                </Route>

                                <Route path="/patient-context/:ptId">
                                    <PatientContext standaloneMode loadOnlyPatient/>
                                </Route>

                                <Route path="/patient">
                                    <Patient/>
                                </Route>

                                <Route path="/wardIpod/:wardId">
                                    <WardViewIpod/>
                                </Route>

                                <Route path="/view/:patientId/:documentIds">
                                    <Viewer/>
                                </Route>

                                <Route path="/holding">
                                    <HoldingPen/>
                                </Route>

                                {process.env.BUILD_MODULE !== 'agyle' &&
                                    <Route path="/search">
                                        <Patients/>
                                    </Route>
                                }

                                {process.env.BUILD_MODULE === 'agyle' &&
                                    <Route path="/search">
                                        <PatientSearch/>
                                    </Route>
                                }

                                {mctEnabled &&
                                    <Route path="/wardCreator/:id">
                                        <WardMapCreator/>
                                    </Route>
                                }

                                <Route path="/audits">
                                    <Audits/>
                                </Route>

                                {bedManagementEnabled &&
                                    <Route path="/bedManagement">
                                        <BedManagement/>
                                    </Route>
                                }

                                <Route path="/admWorkList">
                                    <WorkList/>
                                </Route>

                                <Route path="/fhirDashboard">
                                    <FHIRDashboard fromRoute/>
                                </Route>

                                {(adminModuleEnabled || adminModuleEnabledExceptionUsers.includes(userObject.userName)) && isDesktop && dispatch(canEnterAdminPage()) &&
                                    <Route path="/admin">
                                        <Admin/>
                                    </Route>
                                }

                                {externalManagmentEnabled &&
                                    <Route path="/eadmit">
                                        {/*<Admit/>*/}
                                        <AdmitPatientExt disableHeader/>
                                    </Route>
                                }

                                {externalManagmentEnabled &&
                                    <Route path="/eadmitRefresh">
                                        <AdmitPatientExtRefresh />
                                    </Route>
                                }

                                {registrationEnabled &&
                                    <Route path={MODULE_PATHS[MODULE_NAMES.PATIENT_REGISTRY]}>
                                        <AdmitPatientNewNew/>
                                    </Route>
                                }

                                {registrationEnabled &&
                                    <Route path="/registration">
                                        <Registration/>
                                    </Route>
                                }

                                {extendedDashboardEnabled &&
                                    <Route path="/report/dashboard">
                                        <ExtendedDashboard/>
                                    </Route>
                                }

                                <Route path="/report/estimatedTime/:hospitalId">
                                    <EstimatedWaitingTime/>
                                </Route>

                                {cleaningRequestEnabled &&
                                    <Route path="/report/cleaningRequest">
                                        <CleaningRequest/>
                                    </Route>
                                }

                                {isUHDMap &&
                                    <Route path="/main">
                                        <MainDashboard/>
                                    </Route>
                                }

                                {/*that is a helper to find icons names*/}
                                {process.env.NODE_ENV === 'development' &&
                                    <Route path="/icons">
                                        <AgyleNamedIconView/>
                                    </Route>
                                }

                                {bookAdmissionModuleEnabled && isDesktop &&
                                    <Route path="/bookAdmission">
                                        <BookAdmissionLanding/>
                                    </Route>
                                }

                                {bookAdmissionModuleEnabled && isDesktop &&
                                    <Route path="/createBookAdmission/:patientId/:operationId">
                                        <BookAdmissionForm/>
                                    </Route>
                                }

                                {bookAdmissionModuleEnabled && isDesktop &&
                                    <Route path="/bookedAdmissions">
                                        <BookedAdmissions isFrame={props.isFrame}/>
                                    </Route>
                                }

                                    {bookAdmissionModuleEnabled && isDesktop &&
                                        <Route path="/bookedAdmissionsDashboard">
                                            <BookedAdmissionsDashboard isFrame={props.isFrame}/>
                                        </Route>
                                    }

                                    {isDesktop && (
                                      <Route
                                        path={MODULE_PATHS[MODULE_NAMES.SCHEDULER]}>
                                          <AppointmentsRoot isStandalone={false}/>
                                      </Route>
                                    )}

                                <Route path="/training">
                                    <Tutorials/>
                                </Route>

                                {canBooking &&
                                    <Route path="/bedBooking">
                                        <BedBookingTable/>
                                    </Route>
                                }

                                <Route path="/insight" component={DashboardRoutes} />

                                <Route exact path="/">
                                    {process.env.BUILD_MODULE === 'agyle' &&
                                        <Redirect to={redirect}/>
                                    }
                                    {process.env.BUILD_MODULE !== 'agyle' &&
                                        process.env.BUILD_MODULE !== 'lwh' &&
                                        process.env.BUILD_MODULE !== 'stg' &&
                                        process.env.BUILD_MODULE !== 'dch' &&
                                        process.env.BUILD_MODULE !== 'mse' &&
                                        <Patients/>}
                                    {(process.env.BUILD_MODULE === 'lwh' ||
                                            process.env.BUILD_MODULE === 'stg' ||
                                            process.env.BUILD_MODULE === 'dch' ||
                                            process.env.BUILD_MODULE === 'mse') &&
                                        <HoldingPen/>}
                                </Route>
                                <Route component={Page404}/>
                            </Switch>
                        </Router>
                    </React.Suspense>
                </PerfectScrollbar>
            </div>
        </div>
    );
}

export default App;
